import { useEffect, useState } from "react";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { MarketTicker } from "@/components/brand/MarketTicker";
import { LotCard } from "@/components/brand/LotCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { KAFFA } from "@/constants/testIds";
import { useLocale } from "@/contexts/LocaleContext";

const COFFEE_TYPES = ["Arábica", "Conilon", "Robusta", "Especial", "Microlote", "Gourmet"];
const PROCESSES = ["Natural", "Washed", "Honey", "Pulped Natural", "Anaerobic", "Fermentado"];
const REGIONS = ["Sul de Minas", "Cerrado Mineiro", "Alta Mogiana", "Chapada Diamantina", "Espírito Santo"];

export default function MarketplacePage() {
  const { t, tData } = useLocale();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", coffee_type: "all", process: "all", region: "all" });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.coffee_type !== "all") params.coffee_type = filters.coffee_type;
    if (filters.process !== "all") params.process = filters.process;
    if (filters.region !== "all") params.region = filters.region;
    api.get("/lots/public", { params })
      .then((r) => setLots(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <MarketTicker />

      {/* Compact intro */}
      <section className="border-b border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-12">
          <Link to="/" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-accent flex items-center gap-1.5 mb-5">
            <ArrowLeft className="h-3 w-3" /> {t("nav.home")}
          </Link>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-2">
                {lots.length} {t("lots.active")}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black">
                {t("lots.public_title")}
              </h1>
              <p className="text-muted-foreground mt-2 max-w-2xl text-pretty">{t("lots.public_subtitle")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace */}
      <section className="mx-auto max-w-[1600px] w-full px-4 sm:px-8 py-10 flex-1">
        {/* Filters */}
        <div className="border border-border bg-card mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
          <div className="relative px-4 py-3 flex items-center gap-2 sm:col-span-2 lg:col-span-1">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              data-testid={KAFFA.searchInput}
              placeholder={t("common.search")}
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-8"
            />
          </div>
          <Select value={filters.coffee_type} onValueChange={(v) => setFilters((f) => ({ ...f, coffee_type: v }))}>
            <SelectTrigger data-testid={KAFFA.filterCoffeeType} className="border-0 rounded-none h-12 bg-transparent">
              <SelectValue placeholder={t("lots.filter_type")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")} · {t("lots.filter_type")}</SelectItem>
              {COFFEE_TYPES.map((c) => <SelectItem key={c} value={c}>{tData("coffee", c)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.process} onValueChange={(v) => setFilters((f) => ({ ...f, process: v }))}>
            <SelectTrigger data-testid={KAFFA.filterProcess} className="border-0 rounded-none h-12 bg-transparent">
              <SelectValue placeholder={t("common.process")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")} · {t("lots.filter_process")}</SelectItem>
              {PROCESSES.map((c) => <SelectItem key={c} value={c}>{tData("process", c)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.region} onValueChange={(v) => setFilters((f) => ({ ...f, region: v }))}>
            <SelectTrigger data-testid={KAFFA.filterRegion} className="border-0 rounded-none h-12 bg-transparent">
              <SelectValue placeholder={t("common.region")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")} · {t("lots.filter_region")}</SelectItem>
              {REGIONS.map((c) => <SelectItem key={c} value={c}>{tData("region", c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground font-mono-data text-xs uppercase tracking-wider">
            {t("common.loading")}
          </div>
        ) : lots.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            {t("lots.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {lots.map((lot) => <LotCard key={lot.id} lot={lot} />)}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
